import { Box, Button, Flexbox, Icon, Modal, TextField, Typography } from '@hiiretail/synergy-ui';
import { Controller } from 'react-hook-form';
import { CreateTenantFormData, CreateTenantModalComponent } from './CreateTenantModal.types';
import { useIntl } from 'react-intl';
import { ErrorBox } from '../../../../../common/components/ErrorBox/ErrorBox';
import { useTenantForm } from '../../../hooks/useTenantForm';
import { InlineHint } from '../../../../../common/components/InlineHint/InlineHint';

export const CreateTenantModal: CreateTenantModalComponent = ({
  onClose,
  isOpen,
  createTenant,
  isCreating,
  error,
}) => {
  const intl = useIntl();

  const { control, handleSubmit, formState } = useTenantForm(intl);

  const onFormSubmit = (formData: CreateTenantFormData) => {
    createTenant(formData);
  };

  return (
    <Modal
      data-testid="create-tenant-modal"
      title="Create Tenant"
      onClose={onClose}
      open={isOpen}
      footer={
        <Flexbox container justifyContent="flex-end">
          <Button
            type="submit"
            onClick={handleSubmit(onFormSubmit)}
            disabled={
              (formState?.errors && !!Object.entries(formState?.errors).length) ||
              Object.keys(formState?.dirtyFields).length < 3 ||
              isCreating
            }
          >
            {isCreating ? 'Creating ...' : 'Create'}
          </Button>
        </Flexbox>
      }
    >
      <ErrorBox error={error} />
      <Flexbox container gutter={4} alignItems="flex-start">
        <Flexbox item>
          <Icon.Info size="lg" color={() => '#6E33A9'} />
        </Flexbox>
        <Flexbox item container>
          <Typography variant="body2">
            A Hii Retail Tenant represents an isolated resource boundary on the platform. A Tenant
            can manage their resources but cannot access another Tenant's resources in any way.
          </Typography>
        </Flexbox>
      </Flexbox>
      <Box mt={4} />
      <form>
        <Box mb={2}>
          <Controller
            name="name"
            defaultValue=""
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                label="Name"
                name="name"
                type="text"
                ref={ref}
                value={value}
                onChange={onChange}
                // @ts-ignore
                hint={
                  <span>
                   Full name of the tenant<br />
                    Can not be longer than 200 characters
                  </span>
                }
                error={formState.errors?.name?.message}
              />
            )}
          />
        </Box>

        <Box mb={2}>
          <Controller
            name="alias"
            defaultValue=""
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                label="Alias"
                name="alias"
                type="text"
                ref={ref}
                value={value}
                onChange={onChange}
                // @ts-ignore
                hint={
                  <span>
                    Must be 2-20 chars long, consisting of letters, digits, hyphens.
                    <br />Must begin with a letter and must not start with `hii`
                  </span>
                }
                trailingIcon={
                  <InlineHint>
                    <Typography variant="body2" weight="bold">
                      Alias
                    </Typography>
                    <Typography variant="caption">
                      The alias is used in the URL for accessing the tenant's applications.
                      <br />
                      Ie: {'<tenant-alias>.hiiretail.com'}
                    </Typography>
                  </InlineHint>
                }
                error={formState.errors?.alias?.message}
              />
            )}
          />
        </Box>

        <Box mb={2}>
          <Controller
            name="adminUser"
            defaultValue=""
            control={control}
            render={({ field: { ref, value, onChange } }) => (
              <TextField
                tabIndex={0}
                fullWidth
                label="Administrator email"
                name="adminUser"
                type="text"
                ref={ref}
                value={value}
                placeholder="hiiretail@<tenant-domain-name>"
                // @ts-ignore
                hint={
                  <span>
                    Tenant administrator is a root user.<br/>
                    This user will have access to perform all actions on all IAM resources within the Tenant.
                  </span>
                }
                trailingIcon={
                  <InlineHint>
                    <Typography variant="body2" weight="bold">
                      Tenant admin
                    </Typography>
                    <Typography variant="caption">
                      After the tenant is created and an identity provider is set up the following steps should be taken:
                      <br />
                      1. Create a group and assign it the role <strong>"IAM Admin"</strong>
                      <br />
                      2. Create one or more users
                      <br/>
                      3. Add the users to the newly created group
                      <br />
                      The tenant admin <strong>should no longer be used</strong> to administer the tenant after the completion of these steps.
                    </Typography>
                  </InlineHint>
                }
                onChange={onChange}
                error={formState.errors?.adminUser?.message}
              />
            )}
          />
        </Box>
      </form>
    </Modal>
  );
};
